<script setup lang='ts'>
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';

import TheThemesFilter from '@/components/mode-list-filters/TheThemesFilter.vue';
import TheTypeFilter from '@/components/mode-list-filters/TheTypeFilter.vue';
import TheCalendar from '@/components/mode-list/TheCalendar.vue';
import TheMapSearch from '@/components/mode-map/TheMapSearch.vue';
import { useFiltersStore } from '@/stores/useFiltersStore';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useMetricsStore } from '@/stores/useMetricsStore';

import TheDistributorFilter from './TheDistributorFilter.vue';
import TheDurationFilter from './TheDurationFilter.vue';
import TheMultiselectFilter from './TheMultiselectFilter.vue';
import ThePriceFilter from './ThePriceFilter.vue';
import TheTimeRangeFilter from './TheTimeRangeFilter.vue';

const props = withDefaults(
  defineProps<{
    isModeMap?: boolean;
  }>(),
  {
    isModeMap: false,
  },
);

const { t } = useI18n();

const metricsStore = useMetricsStore();
const { places, themes, types, taxonomyFilter } = storeToRefs(metricsStore);

const filtersStore = useFiltersStore();

const globalConfigurationStore = useGlobalConfigurationStore();
const { globalConfiguration } = storeToRefs(globalConfigurationStore);

const timeRangeOptions = [
  { label: t('timeRange.morning'), minTime: 480, maxTime: 720, count: 0 },
  { label: t('timeRange.afternoon'), minTime: 720, maxTime: 1020, count: 0 },
  { label: t('timeRange.evening'), minTime: 1020, maxTime: 1440, count: 0 },
];
const hasCalendar = computed(() => globalConfiguration.value?.options?.hasCalendar ?? true);
const shouldDisplayPriceDropdown = computed(() => globalConfiguration.value?.options?.hasFilters?.price);

const distributorId = computed(() => taxonomyFilter.value?.filter.filter.distributor_id ?? '');
const taxonomyFilterName = computed(() => {
  const name = taxonomyFilter.value?.name.find(it => it.lang === 'fr-FR')?.value ?? null;
  if (!name)
    return 'Filtre Distributeur';
  return name[0].toUpperCase() + name.substring(1);
});

onBeforeMount(async () => await metricsStore.getDistributorFilter());
</script>

<template>
  <div v-if="props.isModeMap">
    <div class="search">
      <TheMapSearch />
    </div>
    <div v-if="hasCalendar">
      <TheCalendar :columns="1" :rows="1" />
    </div>
    <hr>
    <div v-if="themes.length" class="filter">
      <h3>{{ t('filters.themes', { count: 2 }) }}</h3>
      <div>
        <TheThemesFilter />
      </div>
    </div>
    <hr v-if="themes.length">
    <div v-if="types.length" class="filter">
      <h3>{{ t('filters.types', { count: 2 }) }}</h3>
      <div>
        <TheTypeFilter />
      </div>
    </div>
    <hr v-if="types.length">
  </div>
  <div v-if="shouldDisplayPriceDropdown" class="filter">
    <h3>{{ t('menu.price') }}</h3>
    <div>
      <ThePriceFilter :placeholder="t('menu.duration')" :min="0" :max="1500" :step="10" :average="79" />
    </div>
  </div>
  <hr>
  <div v-if="hasCalendar" class="filter">
    <h3>{{ t('menu.duration') }}</h3>
    <TheDurationFilter :min="0" :max="24" :step="1" />
    <hr>
  </div>
  <div v-if="hasCalendar" class="filter">
    <h3>{{ t('menu.moment') }}</h3>
    <TheTimeRangeFilter :options="timeRangeOptions" />
    <hr>
  </div>
  <div v-if="places.length" class="filter">
    <h3>{{ t('menu.place') }}</h3>
    <TheMultiselectFilter :options="places" @update:model-value="filtersStore.addPlacesFilters" />
  </div>
  <div v-if="taxonomyFilter?.taxonomies.length" class="filter">
    <h3>{{ taxonomyFilterName }}</h3>
    <TheDistributorFilter
      :options="taxonomyFilter?.taxonomies"
      @update:model-value="(data) => filtersStore.handleDistributorFilters(data, distributorId!)"
    />
  </div>
</template>

<style lang="css" scoped>
h3 {
  margin-bottom: var(--yb-spacing-sm);
}

hr {
  margin-block: var(--yb-spacing-base);
  border: 1px solid var(--yb-color-lightgray);
}

.search {
  display: flex;
  padding-bottom: var(--yb-spacing-base);
}
</style>
